import React from 'react';

/**
 * Ripple в духе Material Design, двухфазный:
 *  - pointerdown: круг быстро проявляется в точке нажатия и, если кнопку
 *    держат, медленно дозревает до ~15% финального размера;
 *  - pointerup (в любом месте окна): рост продолжается до полного размера,
 *    круг параллельно гаснет и удаляется.
 *
 * Вешать на onPointerDown. Работает через CSS-transition (.ui-ripple в App.css),
 * поэтому анимацию можно продолжить с любой точки без скачков.
 */

/* Предел роста при удержании */
const HOLD_FRACTION = 0.15;
const HOLD_GROW_MS = 1400;
/* Кривая доигрывания: standard easing Material */
const RELEASE_EASING = 'cubic-bezier(0.4, 0, 0.2, 1), linear';
/* Кривая холда: БЫСТРЫЙ видимый старт (мгновенный отклик на нажатие),
   дальше — медленное дозревание к пределу */
const HOLD_EASING = 'cubic-bezier(0.05, 0.55, 0.25, 1), linear';
/* Проявление круга — почти мгновенное (Material: ~75-100ms) */
const FADE_IN_MS = 80;

export const createRipple = (e: React.PointerEvent<HTMLElement>) => {
  const host = e.currentTarget;
  if ((host as HTMLButtonElement).disabled) return;

  const rect = host.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  // Маленький круг в точке нажатия, растущий до дальнего угла элемента
  const size = 24;
  const dx = Math.max(x, rect.width - x);
  const dy = Math.max(y, rect.height - y);
  const finalScale = (Math.hypot(dx, dy) * 2) / size;

  const ripple = document.createElement('span');
  ripple.className = 'ui-ripple';
  ripple.style.width = ripple.style.height = `${size}px`;
  ripple.style.left = `${x - size / 2}px`;
  ripple.style.top = `${y - size / 2}px`;
  host.appendChild(ripple);

  const pressedAt = performance.now();
  const holdScale = Math.max(finalScale * HOLD_FRACTION, 1.5);
  const removeSafely = () => ripple.remove();

  // Фазы: pending (ждём коммита стартовых стилей) -> holding -> released.
  // Release при быстром клике НЕ ждёт своих rAF — применяется сразу,
  // иначе к задержке добавлялись лишние ~2 кадра.
  let phase: 'pending' | 'holding' | 'released' = 'pending';

  const applyRelease = () => {
    ripple.style.transitionTimingFunction = RELEASE_EASING;
    ripple.style.transitionDuration = '450ms, 420ms';
    ripple.style.transform = `scale(${finalScale})`;
    ripple.style.opacity = '1';
    // Затухание — чуть позже при мгновенном клике, чтобы круг успел проявиться
    const elapsed = performance.now() - pressedAt;
    const fadeDelay = Math.max(0, 180 - elapsed);
    window.setTimeout(() => {
      ripple.style.opacity = '0';
      // Слушатель вешаем ТОЛЬКО после запуска затухания: иначе его ловит
      // transitionend от fade-in и круг удаляется, едва появившись
      ripple.addEventListener('transitionend', (ev) => {
        if (ev.target === ripple && ev.propertyName === 'opacity') removeSafely();
      });
      // Страховка, если transitionend не пришёл (вкладка свёрнута и т.п.)
      window.setTimeout(removeSafely, 600);
    }, fadeDelay);
  };

  // Двойной rAF: стартовые стили (scale 0, opacity 0) должны примениться
  // до включения целевых, иначе transition не сыграет
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (phase === 'released') {
        // Кнопку уже отпустили (быстрый клик) — сразу полная волна,
        // без промежуточной фазы холда
        applyRelease();
        return;
      }
      phase = 'holding';
      ripple.style.transitionTimingFunction = HOLD_EASING;
      ripple.style.transitionDuration = `${HOLD_GROW_MS}ms, ${FADE_IN_MS}ms`;
      ripple.style.transform = `scale(${holdScale})`;
      ripple.style.opacity = '1';
    });
  });

  const release = () => {
    window.removeEventListener('pointerup', release);
    window.removeEventListener('pointercancel', release);
    if (phase === 'holding') {
      // Стили холда уже применены — переключаем цель немедленно, без rAF
      applyRelease();
    } else {
      // Стартовые стили ещё не закоммичены — release применит rAF выше
      phase = 'released';
    }
  };

  window.addEventListener('pointerup', release);
  window.addEventListener('pointercancel', release);
};

export default createRipple;
